import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import ApiService from '../../../../services/ApiService';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import ConfirmationModal from '../../../../components/ModalConfirmation';
import { usePermissions } from '../../../../hooks/usePermissions';
import { HeaderButton } from '../Invoice/InvoiceHeaderActions/style';
import EmitterSheet, { ControlesFicha } from './EmitterSheet';
import NewEmitterDrawer from './NewEmitterDrawer';
import { AMBIENTE_LABEL, SITUACAO_LABEL, certificadoChip, emissorTitulo, extractApiError, formatCnpj } from './helpers';
import {
  Banner,
  Chip,
  ChipRow,
  Column,
  EmitterCnpj,
  EmitterItem,
  EmitterName,
  EmptyState,
  Layout,
  ListHeader,
  ListPanel,
  SectionTitle,
} from './index.style';
import { EmissorPainel, PainelResponse } from './types';

const FiscalSettings = () => {
  const { refresh, setHeaderActions } =
    useOutletContext<{ refresh?: boolean; setHeaderActions?: (acoes: React.ReactNode) => void }>() ?? {};
  const [searchParams, setSearchParams] = useSearchParams();
  const { setFeedback } = useContext(FeedbackContext);
  const { isAdmin } = usePermissions();
  const [painel, setPainel] = useState<PainelResponse | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [selecionadoId, setSelecionadoId] = useState<number | null>(null);
  const [novoAberto, setNovoAberto] = useState(false);
  const [fichaAlterada, setFichaAlterada] = useState(false);
  const [trocaPendente, setTrocaPendente] = useState<number | null>(null);
  const [controles, setControles] = useState<ControlesFicha | null>(null);

  useEffect(() => {
    if (searchParams.get('novo') !== '1') return;
    setNovoAberto(true);
    const next = new URLSearchParams(searchParams);
    next.delete('novo');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!fichaAlterada || !controles) {
      setHeaderActions?.(null);
      return;
    }
    setHeaderActions?.(
      <>
        <HeaderButton type='button' $variant='outline' onClick={controles.descartar} disabled={controles.salvando}>
          Descartar
        </HeaderButton>
        <HeaderButton type='button' onClick={controles.salvar} disabled={controles.salvando}>
          {controles.salvando ? 'Salvando…' : 'Salvar alterações'}
        </HeaderButton>
      </>,
    );
  }, [fichaAlterada, controles, setHeaderActions]);

  useEffect(() => () => setHeaderActions?.(null), [setHeaderActions]);

  const selecionar = (id: number) => {
    if (id === selecionadoId) return;
    if (fichaAlterada) setTrocaPendente(id);
    else setSelecionadoId(id);
  };

  const carregar = useCallback(async (signal?: AbortSignal) => {
    try {
      const { data } = await ApiService.fiscal.getEmissoresPainel();
      if (signal?.aborted) return;
      setPainel(data);
      setErro(null);
      setSelecionadoId((atual) =>
        atual !== null && data.emissores.some((e) => e.id === atual) ? atual : data.emissores[0]?.id ?? null,
      );
    } catch (error) {
      if (!signal?.aborted) setErro(extractApiError(error, 'Não foi possível carregar os emissores.').message);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    carregar(controller.signal);
    return () => controller.abort();
  }, [carregar, refresh]);

  const substituir = (atualizado: EmissorPainel) =>
    setPainel((atual) =>
      atual
        ? { ...atual, emissores: atual.emissores.map((e) => (e.id === atualizado.id ? atualizado : e)) }
        : atual,
    );

  const selecionado = painel?.emissores.find((e) => e.id === selecionadoId) ?? null;

  return (
    <Layout>
      <ListPanel as='aside'>
        <ListHeader>
          <SectionTitle>Emissores</SectionTitle>
        </ListHeader>
        {painel?.emissores.map((emissor) => {
          const cert = certificadoChip(emissor.certificado.estado, emissor.certificado.diasParaVencer);
          const situacao = SITUACAO_LABEL[emissor.situacao];
          return (
            <EmitterItem
              key={emissor.id}
              type='button'
              $active={emissor.id === selecionadoId}
              $inactive={emissor.situacao === 'INATIVO'}
              onClick={() => selecionar(emissor.id)}
            >
              <EmitterName>{emissorTitulo(emissor)}</EmitterName>
              <EmitterCnpj>{formatCnpj(emissor.empresa.cnpj)}</EmitterCnpj>
              <ChipRow>
                <Chip $tone={cert.tone}>{cert.label}</Chip>
                <Chip $tone={situacao.tone}>{situacao.label}</Chip>
                <Chip $tone='info'>{AMBIENTE_LABEL[emissor.ambienteEmissao]}</Chip>
              </ChipRow>
            </EmitterItem>
          );
        })}
        {painel && painel.emissores.length === 0 && <EmptyState>Nenhum emissor nesta conta.</EmptyState>}
        {!painel && !erro && <EmptyState>Carregando…</EmptyState>}
      </ListPanel>

      <Column>
        {erro && <Banner $tone='bad'>{erro}</Banner>}
        {painel && !painel.gatewayDisponivel && (
          <Banner $tone='warn'>
            Nota Gateway indisponível: dados do emissor e numeração de produção não puderam ser lidos agora.
          </Banner>
        )}
        {selecionado && painel && (
          <EmitterSheet
            key={selecionado.id}
            emissor={selecionado}
            onDirtyChange={setFichaAlterada}
            onControles={setControles}
            ambienteServidor={painel.ambienteServidor}
            gatewayDisponivel={painel.gatewayDisponivel}
            isAdmin={isAdmin}
            onUpdated={substituir}
          />
        )}
      </Column>

      {novoAberto && (
        <NewEmitterDrawer
          onClose={() => setNovoAberto(false)}
          onCreated={async (criado, vinculado) => {
            setNovoAberto(false);
            setFeedback({
              isOpen: true,
              statusOk: true,
              message: vinculado ? 'Emissor vinculado ao cadastro do Nota Gateway.' : 'Emissor criado.',
            });
            await carregar();
            setFichaAlterada(false);
            setSelecionadoId(criado.id);
          }}
        />
      )}

      <ConfirmationModal
        isOpen={trocaPendente !== null}
        title='Descartar alterações?'
        message='As alterações não salvas deste emissor serão perdidas.'
        actions={[
          { label: 'Continuar editando', variant: 'outline', onClick: () => setTrocaPendente(null) },
          {
            label: 'Descartar',
            variant: 'danger',
            onClick: () => {
              setFichaAlterada(false);
              setSelecionadoId(trocaPendente);
              setTrocaPendente(null);
            },
          },
        ]}
      />
    </Layout>
  );
};

export default FiscalSettings;
